import { action, observable } from 'mobx';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { IOrganizationDetailsDTO } from '../../../../../../api/organizations.dao';
import { FormVmValueValidationStatus } from '../../../../../../infrastructure/form-viewmodel-controller';
import { OrganizationLinkMapper as Mapper, SplitLink } from './organization-link.mapper';
import { decodeOrganizationLink, encodeOrganizationLink, getConfigByType } from './organization-link.config';


enum OrganizationLinkType {
    Presenter = 'presenter',
    Participant = 'participant'
}


export class OrganizationLinkViewModel extends AbstractFormViewModelController {
    public static initPresenterLink(organizationDetails: IOrganizationDetailsDTO): OrganizationLinkViewModel {
        return this.init(OrganizationLinkType.Presenter, Mapper.toVmInitParams(OrganizationLinkType.Presenter, organizationDetails));
    }
    public static initParticipantLink(organizationDetails: IOrganizationDetailsDTO): OrganizationLinkViewModel {
        return this.init(OrganizationLinkType.Participant, Mapper.toVmInitParams(OrganizationLinkType.Participant, organizationDetails));
    }

    private static init(type: OrganizationLinkType, linkParts: SplitLink): OrganizationLinkViewModel | null {
        if (!linkParts.every(Boolean)) return null;

        return new OrganizationLinkViewModel(type, ...linkParts);
    }

    public static encode(value: string = ''): string {
        return encodeOrganizationLink(value);
    }

    public static decode(value: string = ''): string {
        return decodeOrganizationLink(value);
    }

    public toDTO(): string {
        return this.linkPrefix.concat(this.getEncodedValue()).trimRight();
    }

    public get name(): string {
        return Mapper.getDtoKeyByType(this.type);
    }

    public get value(): string {
        return this.editModeEnabled ? this.getDecodedValue() : this.getEncodedValue();
    }

    @action.bound
    public updateValue(value: string): void {
        this.setValue(this.type, value);
        this.resetApiValidationError();
    }

    @action.bound
    public resetValue(): void {
        this.resetVm();
        this.resetApiValidationError();
    }


    public get apiValidationMessage(): string {
        return this.apiValidationError || '';
    }

    public get editModeEnabled(): boolean {
        return this.editMode;
    }

    @action.bound
    public enableEditMode(): void {
        this.editMode = true;
    }

    @action.bound
    public disableEditMode(): void {
        this.editMode = false;
        this.trimSpacesAndUpdateValue();
    }

    @action.bound
    public setAsInvalidByApi(apiErrMsg: string = ''): void {
        this.apiValidationError = apiErrMsg;
    }

    public get isValid(): boolean {
        return this.validationState.valid === true && !this.apiValidationError;
    }

    public get validationState(): FormVmValueValidationStatus {
        return this.getValueValidationState(this.type);
    }

    private constructor(private readonly type: OrganizationLinkType, public readonly linkPrefix: string, linkValue: string) {
        super({[type]: linkValue}, `${type}${OrganizationLinkViewModel.name}`, getConfigByType(type));
    }

    @observable
    private editMode = false;

    private getDecodedValue(): string {
        return OrganizationLinkViewModel.decode(this.getValue(this.type) as string);
    }
    private getEncodedValue(): string {
        return this.getValue(this.type) as string;
    }

    @observable
    private apiValidationError: string | null = null;

    @action.bound
    private resetApiValidationError(): void {
        this.apiValidationError = null;
    }

    @action
    private trimSpacesAndUpdateValue(): void {
        this.updateValue(this.getDecodedValue().trimRight());
    }

}
